<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.lineId') }}</h2>
      </template>

      <template
        #end
      >
        <Button
          :label="$t('action.crud.exportCsv')"
          icon="pi pi-download"
          class="mr-2 p-button-outlined p-button-rounded"
          :loading="exporting"
          @click="exportAll()"
        />

        <Button
          v-if="isAdmin"
          :label="importWhitelistEnabled ? $t('lineidpage.import') : $t('action.crud.importCsv')"
          icon="pi pi-upload"
          class="mr-2 p-button-outlined p-button-rounded"
          @click="openImportDialog()"
        />

        <Button
          v-if="isAdmin"
          :label="$t('action.crud.create')"
          icon="pi pi-plus"
          class="mr-2 p-button-outlined p-button-rounded"
          @click="toLineIdForm()"
        />

        <Button
          v-if="isAdmin"
          v-show="selectedLines.length > 0"
          :label="$t('lineidpage.delete')"
          icon="pi pi-trash"
          class="p-button-danger mr-2 p-button-rounded"
          @click="selectedLines.length > 1 ? openMultipleDeleteDialog() : openDeleteDialog(selectedLines[0].lineId)"
        />
      </template>
    </Toolbar>

    <Paginator
      v-model:first="actualPage"
      v-model:rows="lineIdFilter.pageable.size"
      :total-records="totalLineIdCount"
      @page="onLineIdPageChange($event)"
    />

    <LineIdAdvancedFilter
      v-model:stakeholder-id="lineIdFilter.filters.stakeholderId.value"
      v-model:mainVenue="lineIdFilter.filters.mainVenue.value"
      v-model:licencePeriod="lineIdFilter.filters.licencePeriod.value"
      v-model:tuningRangeFrom="lineIdFilter.filters.tuningRangeFrom.value"
      v-model:tuningRangeTo="lineIdFilter.filters.tuningRangeTo.value"
      v-model:duplexHigh="lineIdFilter.filters.duplexHigh.value"
      v-model:duplexLow="lineIdFilter.filters.duplexLow.value"
      @validate="getLines"
      @reset="resetFilter"
    />

    <LineIdTable
      v-model:sortField="lineIdFilter.pageable.sortField"
      v-model:sortOrder="lineIdFilter.pageable.sortOrder"
      v-model:filters="lineIdFilter.filters"
      v-model:rows="lineIdFilter.pageable.size"
      v-model:selection="selectedLines"
      paginator
      :value="lines"
      :total-records="totalLineIdCount"
      @sort="getLines"
      @filter="getLines"
      @page="onLineIdPageChange($event)"
      v-model:first="actualPage"
    >
      <template #before>
        <Column
          style="width: 175px; min-width: 175px;"
          :exportable="false"
        >
          <template #body="{data}">
            <Button
              icon="pi pi-eye"
              class="p-button-rounded p-button-secondary mx-1"
              :title="$t('lineidpage.tooltip.detail')"
              @click="showLineIdDetail(data.id)"
            />

            <Button
              v-if="isAdmin"
              icon="pi pi-pencil"
              class="p-button-rounded mx-1"
              :title="$t('action.crud.update')"
              @click="toLineIdForm(data.id)"
            />

            <Button
              v-if="isAdmin"
              icon="pi pi-trash"
              class="p-button-rounded p-button-danger mx-1"
              :title="$t('lineidpage.tooltip.delete')"
              @click="openDeleteDialog(data)"
            />
          </template>
        </Column>
        <Column
          v-if="isAdmin"
          selection-mode="multiple"
          :exportable="false"
          header-style="width: 3rem"
        />
      </template>
    </LineIdTable>
  </div>

  <line-id-delete-dialog
    v-model:visible="dialogDelete"
    :line-id="selectedLineId"
    @delete="getLines"
  />

  <line-id-multiple-delete-dialog
    v-model:visible="dialogMultipleDelete"
    :line-ids="selectedLines"
    @delete="getLines"
  />

  <line-id-import-dialog
    v-model:visible="dialogImport"
    @import="getLines"
  />
</template>

<script>
import LineIdTable from "@/components/lineId/LineIdTable";
import LineIdDeleteDialog from "@/components/lineId/LineIdDeleteDialog";
import LineIdMultipleDeleteDialog from "@/components/lineId/LineIdMultipleDeleteDialog";
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import LineIdAdvancedFilter from "@/components/lineId/LineIdAdvancedFilter";
import Column from "primevue/column";
import LineIdImportDialog from "@/components/lineId/LineIdImportDialog";
import {reactive} from "vue";
import useLineIdFilter from "@/composables/filters/useLineIdFilter";
import Paginator from "primevue/paginator";
import moment from "moment/moment";

export default {
  name: 'LineIdView',
  components: {
    LineIdImportDialog,
    Toolbar,
    Button,
    Column,
    LineIdAdvancedFilter,
    LineIdMultipleDeleteDialog,
    LineIdDeleteDialog,
    LineIdTable,
    Paginator
  },
  setup() {
    const lineIdFilter = reactive(useLineIdFilter());
    return {
      lineIdFilter
    }
  },
  data() {
    return {
      lines: [],
      totalLineIdCount: 0,
      selectedLineId: null,
      selectedLines: [],
      dialogDelete: false,
      dialogMultipleDelete: false,
      dialogImport: false,
      exporting: false,
      actualPage: 1,
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters["auth/userIsAdmin"];
    },
    importWhitelistEnabled() {
      return this.$store.getters["setting/getLineIdImportWhitelistEnabled"];
    }
  },
  mounted() {
    this.getLines();
  },
  methods: {
    resetFilter() {
      this.lineIdFilter.reset();
      this.getLines();
    },
    async getLines() {
      try {
        const params = this.lineIdFilter.getQueryParams();
        const {content, totalElements} = await this.$lineIdService.fetchLinesPagingAndSorting(params);
        this.lines = content;
        this.totalLineIdCount = totalElements;
        this.selectedLines = [];
      } catch (e) {
        console.error(e);
      }
    },

    onLineIdPageChange(event) {
      this.lineIdFilter.pageable.page = event.page;
      this.getLines();
      this.actualPage = event.page * this.lineIdFilter.pageable.size;
    },

    toLineIdForm(id = null) {
      if (id !== null) {
        this.$router.push({name: 'lineIdUpdate', params: {id: id}})
      } else {
        this.$router.push({name: 'lineIdCreate'})
      }
    },
    openDeleteDialog(lineId) {
      this.selectedLineId = lineId
      this.dialogDelete = true
    },
    openMultipleDeleteDialog() {
      this.dialogMultipleDelete = true
    },
    async exportAll(){
      this.exporting = true;
      try {
        const result = await this.$exportService.lineId(this.lineIdFilter.getQueryParams());
        const blob = new Blob([result.content], {type:'text/csv'});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const date = moment(new Date()).format('YYYY-MM-DD_HH-mm-ss');
        link.download = `export_${date}.csv`;
        link.click();
      }catch (e) {
        console.error(e);
        this.$emitter.emit('toast', {severity: 'error', message: 'error.global'})
      }finally {
        this.exporting = false;
      }
    },
    openImportDialog() {
      this.dialogImport = true
    },
    showLineIdDetail(id) {
      this.$router.push({name: "lineIdDetail", params: {id: id}});
    },
  }
}
</script>

<style lang="scss" scoped>
</style>
